import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext, useLocation } from "react-router-dom";
import PatientHistoryForm from "../Forms/PatientHistoryForm";
import { usePatient } from "../../context/PatientContext";
import { useAuth } from "../../context/AuthProvider";
import { useTheme } from "../../context/ThemeContext";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";

const EditPatientHistory = () => {
  const { patientData, setPatientData, selectedCase, doctorInfo } = usePatient();
  const { theme } = useTheme();
  const { patientId, caseId, visitId } = useParams();
  const navigate = useNavigate();
  const [loading] = useState(false);
  const { markSectionComplete } = useOutletContext();
  const { verifyTokenAndRedirect } = useAuth();
  const location = useLocation();

  useEffect(() => {
    verifyTokenAndRedirect();
  }, [verifyTokenAndRedirect]);

  const onSubmitHandler = (updatedHistory, options = {}) => {
    const isAutoSave = options?.isAutoSave;
    const updatedData = {
      ...patientData,
      patientHistory: patientData?.patientHistory?.length > 0
        ? patientData.patientHistory.map(info => ({
          ...info,
          ...updatedHistory,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }))
        : [{
          ...updatedHistory,
          PATIENT_ID: patientData.PATIENT_ID,
          CASE_ID: selectedCase || caseId,
          DOCTOR_ID: doctorInfo.MANDALI_ID,
          CLINIC_ID: doctorInfo.CLINIC_ID
        }]
    };

    setPatientData(updatedData);
    markSectionComplete("history");
    if (!isAutoSave) {
      if (location.pathname.startsWith("/addpatient")) {
        navigate("/addpatient/ci");
      } else if (location.pathname.startsWith("/visits")) {
        navigate(`/visits/${patientId}/${caseId}/${visitId}/ci`);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <Loader2 className="animate-spin mb-4" size={40} style={{ color: theme.primary }} />
        <p className="font-medium opacity-60" style={{ color: theme.text }}>Loading history...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-10">
        <h3
          className="text-2xl md:text-4xl mb-3 tracking-tight"
          style={{
            fontFamily: theme.fonts?.serif,
            color: theme.text
          }}
        >
          Medical History
        </h3>
        <div className="h-1 w-12 rounded-full mb-6" style={{ backgroundColor: theme.primary }} />
        <p className="text-sm md:text-base opacity-60 font-medium" style={{ color: theme.textSecondary }}>
          Documenting past medical conditions, surgeries, and family health patterns.
        </p>
      </div>

      {patientData || (patientId === undefined && caseId === undefined && visitId === undefined) ? (
        <PatientHistoryForm
          initialValues={{ ...patientData, patientHistory: patientData?.patientHistory || [{}], }}
          onSubmit={onSubmitHandler}
          onAutoSave={(values) => onSubmitHandler(values, { isAutoSave: true })}
          enableReinitialize={true}
        />
      ) : (
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="animate-spin mb-4" size={32} style={{ color: theme.primary }} />
          <p className="opacity-60">Initializing form data...</p>
        </div>
      )}
    </div>
  );

};

export default EditPatientHistory;


